import React, { useState } from 'react';
import { 
  Search, 
  User, 
  Building, 
  MapPin, 
  CreditCard, 
  Check, 
  ArrowRight,
  History,
  QrCode,
  Printer,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Avatar from '../components/common/Avatar';
import { mockCompanies } from '../mock/data';
import { useAppContext } from '../contexts/AppContext';
import { Guest } from '../types';

const ReceptionDesk: React.FC = () => {
  const { guests, issueCard, addGuest } = useAppContext();
  const [currentStep, setCurrentStep] = useState<'search' | 'select' | 'issue' | 'register'>('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [foundGuest, setFoundGuest] = useState<Guest | null>(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedFloor, setSelectedFloor] = useState('');
  const [isIssuingCard, setIsIssuingCard] = useState(false);
  const [cardIssued, setCardIssued] = useState(false);
  const [cardId, setCardId] = useState('');
  const [searchResult, setSearchResult] = useState<'found' | 'not-found' | 'not-approved' | null>(null);

  // Get approved guests only
  const approvedGuests = guests.filter(guest => guest.status === 'approved');
  const allGuests = guests;

  const handleSearch = () => {
    console.log('ReceptionDesk - Searching for:', searchTerm);
    console.log('ReceptionDesk - Total guests:', allGuests.length);
    console.log('ReceptionDesk - Approved guests:', approvedGuests.length);
    console.log('ReceptionDesk - All guests data:', allGuests);
    
    // Tìm trong danh sách đã duyệt
    const approvedGuest = approvedGuests.find(g => 
      g.idNumber === searchTerm || 
      g.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (approvedGuest) {
      console.log('ReceptionDesk - Found approved guest:', approvedGuest);
      setFoundGuest(approvedGuest);
      setSelectedCompany(approvedGuest.company);
      const company = mockCompanies.find(c => c.name === approvedGuest.company);
      if (company) {
        setSelectedFloor(company.floor.toString());
      }
      setSearchResult('found');
      setCurrentStep('select');
      return;
    }

    // Tìm trong tất cả khách (bao gồm chưa duyệt)
    const anyGuest = allGuests.find(g => 
      g.idNumber === searchTerm || 
      g.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (anyGuest) {
      console.log('ReceptionDesk - Found guest (not approved):', anyGuest);
      setFoundGuest(anyGuest);
      setSearchResult('not-approved');
    } else {
      setSearchResult('not-found');
    }
  };

  const handleIssueCard = async () => {
    setIsIssuingCard(true);
    
    // Simulate card scanning and issuing process
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const newCardId = `CARD${Date.now().toString().slice(-6)}`;
    setCardId(newCardId);
    setCardIssued(true);
    setIsIssuingCard(false);

    // Update guest with card info using context
    if (foundGuest) {
      const company = mockCompanies.find(c => c.name === selectedCompany);
      issueCard(
        foundGuest.id, 
        newCardId, 
        parseInt(selectedFloor), 
        company?.elevatorZone || 'A'
      );
    }
  };

  const handleReset = () => {
    setCurrentStep('search');
    setSearchTerm('');
    setFoundGuest(null);
    setSelectedCompany('');
    setSelectedFloor('');
    setCardIssued(false);
    setCardId('');
    setSearchResult(null);
  };

  const handleQuickRegister = () => {
    setCurrentStep('register');
  };

  const companyOptions = mockCompanies.map(company => ({
    value: company.name,
    label: `${company.name} - Tầng ${company.floor}`
  }));

  const getElevatorZone = (companyName: string) => {
    const company = mockCompanies.find(c => c.name === companyName);
    return company?.elevatorZone || 'A';
  };

  const getElevatorZoneColor = (zone: string) => {
    switch (zone) {
      case 'A': return 'bg-blue-100 text-blue-700';
      case 'B': return 'bg-green-100 text-green-700';
      case 'C': return 'bg-purple-100 text-purple-700';
      default: return 'bg-secondary-100 text-secondary-700';
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-secondary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-secondary-900">
                Lễ Tân - Cấp Thẻ Khách
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={handleReset}>
                <RefreshCw size={16} className="mr-2" />
                Làm mới
              </Button>
              <div className="flex items-center space-x-2">
                <Avatar size="sm" />
                <span className="text-sm font-medium text-secondary-700">Lễ Tân</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Search & Guest Info */}
          <div className="space-y-6">
            {/* Step 1: Search */}
            <Card>
              <div className="flex items-center space-x-2 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep === 'search' ? 'bg-primary-500 text-white' : 'bg-success-500 text-white'
                }`}>
                  1
                </div>
                <h2 className="text-lg font-semibold text-secondary-900">
                  Tra cứu khách
                </h2>
              </div>
              
              <div className="space-y-4">
                <Input
                  label="Số CCCD hoặc Họ tên"
                  placeholder="Nhập số CCCD hoặc họ tên để tìm kiếm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftIcon={<Search size={20} />}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button 
                  size="lg" 
                  onClick={handleSearch}
                  disabled={!searchTerm.trim()}
                  className="w-full"
                >
                  <Search size={16} className="mr-2" />
                  Tìm kiếm
                </Button>

                {/* Search Results */}
                {searchResult === 'not-found' && (
                  <div className="mt-4 p-4 bg-warning-50 border border-warning-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-warning-600" />
                      <span className="font-medium text-warning-800">Không tìm thấy thông tin</span>
                    </div>
                    <p className="text-warning-700 text-sm mb-3">
                      CCCD/Tên này chưa có trong hệ thống. Bạn có muốn đăng ký nhanh không?
                    </p>
                    <Button variant="warning" size="sm" onClick={handleQuickRegister}>
                      Đăng ký nhanh
                    </Button>
                  </div>
                )}

                {searchResult === 'not-approved' && foundGuest && (
                  <div className="mt-4 p-4 bg-danger-50 border border-danger-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-danger-600" />
                      <span className="font-medium text-danger-800">Chưa được duyệt</span>
                    </div>
                    <div className="text-sm text-danger-700 mb-3">
                      <p><strong>{foundGuest.fullName}</strong> đã đăng ký nhưng chưa được BQL duyệt.</p>
                      <p>Trạng thái: <Badge variant={foundGuest.status === 'pending' ? 'warning' : 'danger'}>
                        {foundGuest.status === 'pending' ? 'Chờ duyệt' : 'Từ chối'}
                      </Badge></p>
                    </div>
                    <p className="text-xs text-danger-600">
                      Vui lòng liên hệ BQL để duyệt đăng ký trước khi cấp thẻ.
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Guest Information */}
            {foundGuest && (
              <Card>
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Thông tin khách
                </h3>
                <div className="flex items-start space-x-4">
                  <Avatar src={foundGuest.photo} size="xl" />
                  <div className="flex-1">
                    <h4 className="text-xl font-semibold text-secondary-900 mb-2">
                      {foundGuest.fullName}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <User size={14} className="text-secondary-500" />
                        <span>CCCD: {foundGuest.idNumber}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Building size={14} className="text-secondary-500" />
                        <span>{foundGuest.company}</span>
                      </div>
                      {foundGuest.contactPerson && (
                        <div className="flex items-center space-x-2">
                          <User size={14} className="text-secondary-500" />
                          <span>Gặp: {foundGuest.contactPerson}</span>
                        </div>
                      )}
                      {foundGuest.visitReason && (
                        <div className="text-secondary-600">
                          Lý do: {foundGuest.visitReason}
                        </div>
                      )}
                    </div>
                    <div className="mt-3">
                      {foundGuest.status === 'approved' && (
                        <Badge variant="success">Đã được duyệt</Badge>
                      )}
                      {foundGuest.status === 'pending' && (
                        <Badge variant="warning">Chờ duyệt</Badge>
                      )}
                      {foundGuest.status === 'rejected' && (
                        <Badge variant="danger">Từ chối</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Visit History */}
            {foundGuest && foundGuest.status === 'approved' && (
              <Card>
                <div className="flex items-center space-x-2 mb-4">
                  <History size={20} className="text-secondary-500" />
                  <h3 className="text-lg font-semibold text-secondary-900">
                    Lịch sử ra vào
                  </h3>
                </div>
                <div className="space-y-2 text-sm">
                  {/* Hiển thị lịch sử dựa trên data thực tế */}
                  {foundGuest.checkInTime && (
                    <div className="flex justify-between items-center py-2 border-b border-secondary-100">
                      <span>
                        {new Date(foundGuest.checkInTime).toLocaleDateString('vi-VN') === new Date().toLocaleDateString('vi-VN') 
                          ? 'Hôm nay' 
                          : 'Hôm qua'} - {new Date(foundGuest.checkInTime).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <Badge variant="success" size="sm">Vào</Badge>
                    </div>
                  )}
                  {foundGuest.checkOutTime && (
                    <div className="flex justify-between items-center py-2 border-b border-secondary-100">
                      <span>
                        {new Date(foundGuest.checkOutTime).toLocaleDateString('vi-VN') === new Date().toLocaleDateString('vi-VN') 
                          ? 'Hôm nay' 
                          : 'Hôm qua'} - {new Date(foundGuest.checkOutTime).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <Badge variant="secondary" size="sm">Ra</Badge>
                    </div>
                  )}
                  {!foundGuest.checkInTime && !foundGuest.checkOutTime && (
                    <div className="text-center py-4 text-secondary-500">
                      Chưa có lịch sử ra vào
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>

          {/* Right Panel - Company Selection & Card Issue */}
          <div className="space-y-6">
            {/* Step 2: Select Company & Floor */}
            {currentStep !== 'search' && (
              <Card>
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === 'select' ? 'bg-primary-500 text-white' : 'bg-success-500 text-white'
                  }`}>
                    2
                  </div>
                  <h2 className="text-lg font-semibold text-secondary-900">
                    Chọn công ty & tầng
                  </h2>
                </div>

                <div className="space-y-4">
                  <Select
                    label="Công ty"
                    options={companyOptions}
                    value={selectedCompany}
                    onChange={(e) => {
                      setSelectedCompany(e.target.value);
                      const company = mockCompanies.find(c => c.name === e.target.value);
                      if (company) {
                        setSelectedFloor(company.floor.toString());
                      }
                    }}
                  />
                  
                  <Input
                    label="Tầng"
                    type="number"
                    value={selectedFloor}
                    onChange={(e) => setSelectedFloor(e.target.value)}
                    leftIcon={<MapPin size={20} />}
                  />

                  {selectedCompany && (
                    <div className="p-4 bg-secondary-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-secondary-700">
                          Khu vực thang máy:
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          getElevatorZoneColor(getElevatorZone(selectedCompany))
                        }`}>
                          Khu {getElevatorZone(selectedCompany)}
                        </span>
                      </div>
                    </div>
                  )}

                  <Button
                    size="lg"
                    onClick={() => setCurrentStep('issue')}
                    disabled={!selectedCompany || !selectedFloor}
                    className="w-full"
                  >
                    Tiếp tục cấp thẻ
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </Card>
            )}

            {/* Step 3: Issue Card */}
            {currentStep === 'issue' && (
              <Card>
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-medium">
                    3
                  </div>
                  <h2 className="text-lg font-semibold text-secondary-900">
                    Cấp thẻ khách
                  </h2>
                </div>

                {!cardIssued ? (
                  <div className="text-center space-y-6">
                    {!isIssuingCard ? (
                      <>
                        <div className="mx-auto w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                          <CreditCard className="w-12 h-12 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                            Sẵn sàng cấp thẻ
                          </h3>
                          <div className="bg-secondary-50 p-4 rounded-lg mb-4">
                            <h4 className="font-medium text-secondary-900 mb-2">Thông tin sẽ được ghi vào thẻ:</h4>
                            <div className="text-sm text-secondary-700 space-y-1">
                              <div>• Khách: {foundGuest?.fullName}</div>
                              <div>• CCCD: {foundGuest?.idNumber}</div>
                              <div>• Công ty: {selectedCompany}</div>
                              <div>• Tầng: {selectedFloor}</div>
                              <div>• Khu thang máy: {getElevatorZone(selectedCompany)}</div>
                            </div>
                          </div>
                          <p className="text-secondary-600 mb-4">
                            <strong>Bước 1:</strong> Đặt thẻ trống lên đầu đọc<br/>
                            <strong>Bước 2:</strong> Nhấn nút bên dưới để bắt đầu
                          </p>
                          <Button size="lg" onClick={handleIssueCard}>
                            <CreditCard size={16} className="mr-2" />
                            Quét thẻ trống & Mapping
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mx-auto w-24 h-24 bg-warning-100 rounded-full flex items-center justify-center">
                          <div className="animate-spin">
                            <CreditCard className="w-12 h-12 text-warning-600" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                            Đang mapping thông tin vào thẻ...
                          </h3>
                          <div className="space-y-2 text-sm text-secondary-600">
                            <div>🔍 Đang quét thẻ trống...</div>
                            <div>📝 Ghi thông tin khách vào thẻ...</div>
                            <div>🔗 Mapping với cơ sở dữ liệu...</div>
                            <div>✅ Kích hoạt quyền truy cập...</div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center space-y-6">
                    <div className="mx-auto w-24 h-24 bg-success-100 rounded-full flex items-center justify-center">
                      <Check className="w-12 h-12 text-success-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-success-700 mb-2">
                        Cấp thẻ thành công!
                      </h3>
                      <p className="text-secondary-600 mb-4">
                        Mã thẻ: <span className="font-mono font-semibold">{cardId}</span>
                      </p>
                    </div>
                    
                    <div className="bg-success-50 p-4 rounded-lg text-left border border-success-200">
                      <h4 className="font-semibold text-success-900 mb-3">
                        ✅ Thông tin thẻ đã được mapping thành công:
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="font-medium text-secondary-700">Mã thẻ:</span>
                          <div className="font-mono text-primary-600 font-bold">{cardId}</div>
                        </div>
                        <div>
                          <span className="font-medium text-secondary-700">Khách:</span>
                          <div className="text-secondary-900">{foundGuest?.fullName}</div>
                        </div>
                        <div>
                          <span className="font-medium text-secondary-700">CCCD:</span>
                          <div className="text-secondary-900">{foundGuest?.idNumber}</div>
                        </div>
                        <div>
                          <span className="font-medium text-secondary-700">Công ty:</span>
                          <div className="text-secondary-900">{selectedCompany}</div>
                        </div>
                        <div>
                          <span className="font-medium text-secondary-700">Tầng truy cập:</span>
                          <div className="text-lg font-bold text-primary-600">{selectedFloor}</div>
                        </div>
                        <div>
                          <span className="font-medium text-secondary-700">Khu thang máy:</span>
                          <div className={`inline-block px-2 py-1 rounded text-sm font-medium ${getElevatorZoneColor(getElevatorZone(selectedCompany))}`}>
                            Khu {getElevatorZone(selectedCompany)}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <span className="font-medium text-secondary-700">Thời gian cấp:</span>
                          <div className="text-secondary-900">{new Date().toLocaleString('vi-VN')}</div>
                        </div>
                      </div>
                      
                      <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="text-xs text-blue-800">
                          <strong>Hướng dẫn cho khách:</strong> Vui lòng đến <strong>Khu thang máy {getElevatorZone(selectedCompany)}</strong> 
                          để lên tầng <strong>{selectedFloor}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <Button variant="secondary" size="sm">
                        <QrCode size={16} className="mr-2" />
                        In QR Code
                      </Button>
                      <Button variant="secondary" size="sm">
                        <Printer size={16} className="mr-2" />
                        In phiếu
                      </Button>
                      <Button size="sm" onClick={handleReset}>
                        Hoàn tất
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Quick Registration Form */}
            {currentStep === 'register' && (
              <Card>
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-warning-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    R
                  </div>
                  <h2 className="text-lg font-semibold text-secondary-900">
                    Đăng ký nhanh
                  </h2>
                </div>

                <div className="space-y-4">
                  <Input
                    label="Họ và tên *"
                    placeholder="Nhập họ và tên"
                    leftIcon={<User size={20} />}
                  />
                  <Input
                    label="Số CCCD/CMND *"
                    placeholder="Nhập số CCCD/CMND"
                    value={searchTerm}
                    readOnly
                  />
                  <Input
                    label="Số điện thoại *"
                    placeholder="Nhập số điện thoại"
                    leftIcon={<User size={20} />}
                  />
                  <Select
                    label="Công ty cần liên hệ *"
                    placeholder="Chọn công ty"
                    options={companyOptions}
                  />
                  <Input
                    label="Người cần gặp"
                    placeholder="Nhập tên người cần gặp"
                    leftIcon={<User size={20} />}
                  />
                </div>

                <div className="flex justify-between mt-6">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={handleReset}
                  >
                    Hủy
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => {
                      // Simulate quick registration
                      alert('Đăng ký thành công! Khách đã được tự động duyệt và có thể cấp thẻ.');
                      handleReset();
                    }}
                  >
                    Đăng ký & Duyệt ngay
                  </Button>
                </div>
              </Card>
            )}

            {/* Instructions */}
            {currentStep !== 'register' && (
              <Card className="bg-blue-50 border-blue-200">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">
                      Hướng dẫn sử dụng:
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>1. Nhập CCCD hoặc tên khách để tìm kiếm</li>
                      <li>2. Kiểm tra thông tin và chọn công ty/tầng</li>
                      <li>3. Quét thẻ trống để cấp thẻ cho khách</li>
                      <li>4. Hướng dẫn khách đến khu thang máy tương ứng</li>
                      <li>5. Nếu chưa đăng ký, có thể đăng ký nhanh</li>
                    </ul>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceptionDesk;
